<template>
  <protected :with="Permission.READ_USERS" default-message>
    <div class="admin-users">
      <admin-view-header title="Użytkownicy">
        <protected :with="Permission.CREATE_USER">
          <button class="button is-success" @click="openNewUserModal">
            <icon name="fa-plus" />
            <span>Nowy użytkownik</span>
          </button>
        </protected>
      </admin-view-header>

      <loader :state="[actions.authorities, actions.users]" without-loading>
        <table-data
          class="is-fullwidth"
          :header="['Użytkownik', 'Akcja']"
          :data-fields="['mailAddress']"
          :data="users"
        >
          <template v-slot="user">
            <td>
              <protected :with="Permission.UPDATE_USER">
                <icon
                  name="fa-pen"
                  class="icon-control"
                  data-tooltip="Edytuj dane użytkownika"
                  @click="openEditUserModal(user)"
                />
              </protected>
              <protected :with="Permission.DELETE_USER">
                <icon
                  name="fa-trash"
                  class="icon-control is-danger"
                  data-tooltip="Usuń użytkownika"
                  @click="openUserRemovalConfirmationDialog(user)"
                />
              </protected>
            </td>
          </template>
        </table-data>
        <pagination :pageable="pageable" @change-page="requestUsers" />

        <template #error>
          <div class="grey-message">
            <icon name="fa-times-circle" />
            <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
          </div>
        </template>
      </loader>
    </div>
  </protected>
</template>

<script>
import { mapState } from "vuex";
import { Icon, TableData, Pagination } from "%helpers";

import {
  REQUEST_USERS,
  REQUEST_AUTHORITIES,
  REQUEST_USER,
  ADD_USER,
  UPDATE_USER,
  DELETE_USER
} from ":actions";

import { YesNoCancelModal, UserModal } from "%modals";

import { Protected, WithDispatcherProgress } from "%mixins";

import { SuccessNotification } from "%notifications";

import AdminViewHeader from "%admin/components/global/AdminViewHeader";

import { omit } from "%utils/object";

export default {
  name: "admin-users",

  mixins: [Protected, WithDispatcherProgress],

  components: {
    Icon,
    AdminViewHeader,
    TableData,
    Pagination
  },

  computed: mapState({
    users: state => state.users.users.content,
    pageable: state => omit(state.users.users, "content"),
    authorities: state => state.users.authorities
  }),

  beforeMount() {
    this.requestUsers(1);
    this.dispatchWithProgress("authorities", REQUEST_AUTHORITIES);
  },

  methods: {
    successNotification(content) {
      this.$emit("push-notification", {
        component: SuccessNotification,
        props: { content }
      });
    },

    requestUsers(page) {
      this.dispatchWithProgress("users", REQUEST_USERS, { page });
    },

    openNewUserModal() {
      this.$emit("show-modal", {
        component: UserModal,
        props: {
          authorities: this.authorities,
          onSubmit: user => this.addUser(user)
        }
      });
    },

    addUser(user) {
      return this.$store
        .dispatch(ADD_USER, { user })
        .then(() =>
          this.successNotification(
            `Konto użytkownika <strong>${user.mailAddress}</strong> zostało utworzone`
          )
        );
    },

    updateUser(mailAddress, user) {
      return this.$store
        .dispatch(UPDATE_USER, { user, mailAddress })
        .then(() =>
          this.successNotification(
            `Konto użytkownika <strong>${mailAddress}</strong> zostało zaktualizowane`
          )
        );
    },

    deleteUser({ mailAddress }) {
      return this.$store.dispatch(DELETE_USER, { mailAddress });
    },

    async openEditUserModal({ mailAddress }) {
      const response = await this.$store.dispatch(REQUEST_USER, {
        mailAddress
      });
      this.$emit("show-modal", {
        component: UserModal,
        props: {
          user: response.data,
          authorities: this.authorities,
          onSubmit: (user, mail) => this.updateUser(mail, user)
        }
      });
    },

    openUserRemovalConfirmationDialog(user) {
      this.$emit("show-modal", {
        component: YesNoCancelModal,
        props: {
          title: "Potwierdzenie usunięcia",
          content: `<p>Czy na pewno chcesz usunąć użytkownika <code>${user.mailAddress}</code>?</p>`,

          proceed: {
            type: "is-danger",
            icon: "fa-trash",
            content: "Usuń",
            callback: () => this.deleteUser(user)
          },

          cancel: {
            content: "Anuluj"
          }
        }
      });
    }
  }
};
</script>

<style></style>
